import { Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { SubscriptionManager } from '@sama/rxjs-core';
import { findKeyLocationIndex } from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { UrlRequestService } from '../../../../cross-team-shared/misc-services/resources/url-request.service';
import { TokenManagementService } from '../../../../cross-team-shared/misc-services/token-management.service';
import { isVideoOutput } from '../../../../cross-team-shared/misc-utils/output-utils';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../../team-apis/core/shell-connector';
import { MLExtrapolationResponse } from '../../../../team-apis/raccoons/ml-extrapolation-response';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { ExtrapolationService } from './extrapolation.service';

export interface RequestOptions {
  shapeIndex: number;
  startKeyFrame: number;
  requestedExtrapolatedFrameCount: number;
}

export class ExtrapolationRequest extends SubscriptionManager {
  private store: AbstractSensorContentStore2D;
  private urlRequestService: UrlRequestService;
  private tokenService: TokenManagementService;
  private shellConnector: ShellConnector;
  private extrapolationService: ExtrapolationService;

  constructor(
    public requestOptions: RequestOptions,
    injector: Injector,
  ) {
    super();
    this.store = injector.get(AbstractSensorContentStore2D);
    this.extrapolationService = injector.get(ExtrapolationService);
    this.urlRequestService = injector.get(UrlRequestService);
    this.tokenService = injector.get(TokenManagementService);
    this.shellConnector = injector.get(shellConnectorToken);

    const keyLocationStore = this.store('shapes')(requestOptions.shapeIndex)(
      'key_locations',
    );
    const keyIndex = findKeyLocationIndex(
      keyLocationStore.state(),
      requestOptions.startKeyFrame,
    );

    this.subscribeTo(keyLocationStore(keyIndex).$, (keyFrameStore) => {
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      if (!keyFrameStore || keyFrameStore.visibility === 0) {
        this.cleanUp();
      }
    });
  }

  async send() {
    const requestOptions = await this.buildRequestOptions();

    // if a shape is deleted before the token request completes
    if (!requestOptions) {
      return undefined;
    }

    this.extrapolationService.addInFlightRequest(this);

    const { output } = this.store.state();
    assert(
      isVideoOutput(output),
      'ExtrapolationService should not have created this request for an image output',
    );

    const extrapolationRequestUrl =
      output.meta.ml_extrapolation_options.model_id;

    return this.urlRequestService.request<MLExtrapolationResponse>(
      'POST',
      extrapolationRequestUrl,
      requestOptions,
    );
  }

  cleanUp() {
    this.extrapolationService.deleteInFlightRequest(this);
    this.unsubscribe();
  }

  private async buildRequestOptions() {
    const token = await this.tokenService.getToken('ml_infer');
    const shape = this.getShapeAnswer();
    if (!shape) {
      return undefined;
    }
    return {
      headers: {
        'Content-type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: {
        asset_url: this.store.getInputUrl(),
        start: this.requestOptions.startKeyFrame + 1,
        end:
          this.requestOptions.startKeyFrame +
          this.requestOptions.requestedExtrapolatedFrameCount,
        shape,
        user_id: this.store.state().currentUser.id,
        project_id: this.store.state().project.id,
        task_id: this.store.state().task.id,
        output_id: this.store.state().output.id,
        round: this.store.state().task.round,
        step_id: this.store.state().stepId,
      },
    };
  }

  private getShapeAnswer() {
    const shape = this.store.state().shapes[this.requestOptions.shapeIndex];
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (shape) {
      return this.shellConnector.convertShapeStateToAnswer(shape);
    }
    return undefined;
  }
}
